import React, { useState } from "react";
import classes from "./addRole.module.scss";
import { Close, Delete, Edit, Save } from "@mui/icons-material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import Divider from "@mui/material/Divider";
import {
  Button,
  Checkbox,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  useGetRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
} from "../../../api/roleApi";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import DataGridComp from "../../datagrid/DataGrid";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { User } from "../../userControlComp/UserControlComp";
import toast from "react-hot-toast";

interface AddRoleProps {
  onClose: () => void;
}

interface Permissions {
  can_read: boolean;
  can_write: boolean;
  can_delete: boolean;
}

interface Role {
  role_id: number;
  role_type: string;
  permissions: Permissions | null;
}

const AddRole: React.FC<AddRoleProps> = ({ onClose }) => {
  const user = useSelector(
    (state: RootState) => state.auth.user as User | null
  );

  const { data: rows = [] } = useGetRolesQuery();
  const [createRole] = useCreateRoleMutation();
  const [updateRole] = useUpdateRoleMutation();
  const [deleteRole] = useDeleteRoleMutation();

  const [localRows, setLocalRows] = useState<Role[]>([]);
  const [editRowId, setEditRowId] = useState<number | null>(null);
  const [editedPermissions, setEditedPermissions] = useState<
    Record<number, Permissions>
  >({});
  const [editedRoleType, setEditedRoleType] = useState<Record<number, string>>(
    {}
  );

  // delete modal states
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  const combinedRows = [...rows, ...localRows];
  const existingIds = rows.map((r) => r.role_id);

  // Add Role
  const handleAddRole = () => {
    if (editRowId !== null) return;
    const tempId =
      Math.max(0, ...existingIds, ...localRows.map((r) => r.role_id)) + 1;
    const newRole: Role = {
      role_id: tempId,
      role_type: "",
      permissions: { can_read: false, can_write: false, can_delete: false },
    };
    setLocalRows((prev) => [...prev, newRole]);
    setEditRowId(tempId);
    setEditedRoleType((prev) => ({ ...prev, [tempId]: newRole.role_type }));
    setEditedPermissions((prev) => ({
      ...prev,
      [tempId]: newRole.permissions!,
    }));
  };

  // Save Role
  const handleSaveClick = async (row: Role) => {
    const roleType = editedRoleType[row.role_id]?.trim();
    if (!roleType) {
      toast.error("Role Type cannot be empty");
      return;
    }

    const updatedData = {
      role_type: roleType,
      permissions: editedPermissions[row.role_id],
    };

    try {
      let response;
      if (rows.find((r) => r.role_id === row.role_id)) {
        response = await updateRole({ id: row.role_id, ...updatedData }).unwrap();
      } else {
        response = await createRole(updatedData).unwrap();
      }

      toast.success(response?.message || "Success");
    } catch (err: any) {
      console.error(err);
      const errorMsg =
        err?.data?.message || err?.message || "Failed to save role";
      toast.error(errorMsg);
    }

    setLocalRows((prev) => prev.filter((r) => r.role_id !== row.role_id));
    setEditRowId(null);
    cleanupEditStates(row.role_id);
  };

  const handleCancel = (roleId: number) => {
    setLocalRows((prev) => prev.filter((r) => r.role_id !== roleId));
    setEditRowId(null);
    cleanupEditStates(roleId);
  };

  const cleanupEditStates = (roleId: number) => {
    setEditedPermissions((prev) => {
      const copy = { ...prev };
      delete copy[roleId];
      return copy;
    });
    setEditedRoleType((prev) => {
      const copy = { ...prev };
      delete copy[roleId];
      return copy;
    });
  };

  // delete confirmation flow
  const confirmDelete = (role: Role) => {
    setSelectedRole(role);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedRole) {
      try {
        const response = await deleteRole(selectedRole.role_id).unwrap();
        toast.success(response?.message || "Role deleted successfully");
      } catch (err: any) {
        const errorMsg =
          err?.data?.message || err?.message || "Failed to delete role";
        toast.error(errorMsg);
      }
    }
    setOpenDeleteModal(false);
    setSelectedRole(null);
  };

  const handleCancelDelete = () => {
    setOpenDeleteModal(false);
    setSelectedRole(null);
  };

  const handleCheckboxChange = (
    roleId: number,
    field: keyof Permissions,
    checked: boolean
  ) => {
    setEditedPermissions((prev) => ({
      ...prev,
      [roleId]: {
        can_read: prev[roleId]?.can_read ?? false,
        can_write: prev[roleId]?.can_write ?? false,
        can_delete: prev[roleId]?.can_delete ?? false,
        [field]: checked,
      },
    }));
  };

  const columns: GridColDef<Role>[] = [
    { field: "role_id", headerName: "Role ID", width: 110 },
    {
      field: "role_type",
      headerName: "Role Type",
      width: 170,
      renderCell: (params: GridRenderCellParams<Role>) => {
        const row = params.row;
        const isEditing = editRowId === row.role_id;

        return isEditing ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <TextField
              size="small"
              placeholder="New Role"
              value={editedRoleType[row.role_id] ?? row.role_type}
              onChange={(e) =>
                setEditedRoleType((prev) => ({
                  ...prev,
                  [row.role_id]: e.target.value,
                }))
              }
              InputProps={{
                style: {
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  padding: "5px 6px",
                },
              }}
              sx={{
                "& .MuiInputBase-root": {
                  height: "35px",
                  display: "flex",
                  alignItems: "center",
                },
              }}
            />
          </div>
        ) : (
          <span style={{ fontFamily: "Poppins" }}>{row.role_type}</span>
        );
      },
    },
    // Permissions
    ...(["can_read", "can_write", "can_delete"] as (keyof Permissions)[]).map(
      (field) => ({
        field,
        headerName: field.replace("_", " ").toUpperCase(),
        width: 150,
        renderCell: (params: GridRenderCellParams<Role>) => {
          const row = params.row;
          const isEditing = editRowId === row.role_id;

          const value = isEditing
            ? editedPermissions[row.role_id]?.[field] ?? false
            : row.permissions?.[field];

          return isEditing ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <Checkbox
                checked={value || false}
                onChange={(e) =>
                  handleCheckboxChange(row.role_id, field, e.target.checked)
                }
              />
            </div>
          ) : value ? (
            "✔"
          ) : (
            "✖"
          );
        },
      })
    ),
    {
      field: "action",
      headerName: "Action",
      width: 140,
      renderCell: (params: GridRenderCellParams<Role>) => {
        const row = params.row;
        const isEditing = editRowId === row.role_id;

        // 🚫 Prevent edit/delete for Admin role (role_id = 1)
        if (row.role_id === 1) {
          return (
            <span style={{ fontFamily: "Poppins", color: "gray" }}>
              Restricted
            </span>
          );
        }

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {isEditing ? (
              <>
                <Save
                  fontSize="small"
                  onClick={() => handleSaveClick(row)}
                  style={{ cursor: "pointer" }}
                />
                <Close
                  fontSize="small"
                  onClick={() => handleCancel(row.role_id)}
                  style={{ cursor: "pointer" }}
                />
              </>
            ) : (
              <>
                {user?.can_write && (
                  <Edit
                    fontSize="small"
                    onClick={() => {
                      setEditRowId(row.role_id);
                      setEditedRoleType((prev) => ({
                        ...prev,
                        [row.role_id]: row.role_type,
                      }));
                      setEditedPermissions((prev) => ({
                        ...prev,
                        [row.role_id]: row.permissions!,
                      }));
                    }}
                    style={{ cursor: "pointer" }}
                  />
                )}
                {user?.can_delete && (
                  <Delete
                    onClick={() => confirmDelete(row)}
                    fontSize="small"
                    style={{ cursor: "pointer" }}
                  />
                )}
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className={classes.addRole}>
      <div className={classes.addRole__heading}>
        <h3>Manage Roles</h3>
        <Close sx={{ color: "red", cursor: "pointer" }} onClick={onClose} />
      </div>

      <Divider sx={{ width: "100%" }} />

      <DataGridComp
        rows={combinedRows}
        columns={columns}
        getRowId={(row) => row.role_id}
        centerFields={["can_read", "can_write", "can_delete", "action"]}
      />

      <div className={classes.addRole__buttonBox}>
        <Button
          onClick={handleAddRole}
          variant="contained"
          sx={{ fontSize: "12px", fontFamily: "Poppins" }}
          startIcon={
            <PersonAddIcon style={{ color: "#fff", fontSize: "14px" }} />
          }
          disabled={editRowId !== null}
        >
          Add Role
        </Button>
      </div>

      {/* Delete confirmation modal */}
      <Dialog
        open={openDeleteModal}
        onClose={handleCancelDelete}
        sx={{
          "& *": { fontFamily: "Poppins !important" },
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete{" "}
          <strong>{selectedRole?.role_type}</strong> role?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelDelete}
            sx={{
              backgroundColor: "#3c4b6d",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#2e3a54",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AddRole;
